import { useCallback } from 'react';
import get from 'lodash/get';
import routePaths from 'router/route-paths';
import { useHistory } from 'react-router-dom';
import { messageOf } from 'utils/error';
import { Role, type LoginWithPasswordMutationVariables } from 'graphql/generated/graphql';
import useLogout from './useLogout';
import type { TAuthResult, TAuth } from './useAuth';

/** The formik actions the submit handler uses. */
export interface IAuthFormActions {
  setSubmitting: (isSubmitting: boolean) => void;
  setErrors: (errors: { form?: string }) => void;
}

const useAuthFormSubmit = (auth: TAuth) => {
  const logout = useLogout();
  const history = useHistory();

  return useCallback(
    async (
      variables: LoginWithPasswordMutationVariables,
      { setSubmitting, setErrors }: IAuthFormActions,
    ): Promise<TAuthResult | undefined> => {
      setSubmitting(true);
      try {
        const data = await auth(variables);
        const noRoles: Role[] = [];
        const roles = get(data, 'user.roles', noRoles);
        if (data && !roles.includes(Role.admin)) {
          logout();
        } else if (roles.includes(Role.admin)) {
          return data;
        }
      } catch (error) {
        if (messageOf(error) === 'AUTH_OTP_REQUIRED_ERROR')
          history.push(routePaths.loginTwoFA, { user: variables });
        setErrors({ form: messageOf(error) });
      } finally {
        setSubmitting(false);
      }
      return undefined;
    },
    [auth, history, logout],
  );
};

export default useAuthFormSubmit;
